/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { NOT_APPLICABLE } from '@config/constants/common';
import { TSite } from '@global-types/index';
import { apiClient } from '@lib/api-client';
import { QueryConfig } from '@lib/react-query';
import useStore from '@store/index';
import {
	queryOptions,
	useQuery,
} from '@tanstack/react-query';
import { getCountryName } from '@utils/countries';
import { getCurrencyUnit } from '@utils/currency-units';
import { formatNumberWithSpaces } from '@utils/formats';
import { formatDate } from '@utils/functions';
import { getTechnologyProject } from '@utils/project-technologies';
import { getUnitLabel } from '@utils/units';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */
type UseAdditionalityProjectOptions = {
	projectId: string;
	queryConfig?: QueryConfig<
		typeof getAdditionalityProjectQueryOptions
	>;
};

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */

export const getPublicAdditionalityProjectById = (
	projectId: string
): Promise<any> => {
	return apiClient.get(
		`/public/additionalities/${projectId}`,
		{
			params: {
				populate:
					'organization,entity,organization.logo, organization.logo.conversionFactors, cover, logo, publicSpecs, internalSpecs, supportedBy.logo',
			},
		}
	);
};

export const getAdditionalityProjectQueryOptions = (
	projectId: string,
	user: any
) => {
	return queryOptions({
		queryKey: ['additionality-projects', projectId],
		queryFn: () => {
			return getPublicAdditionalityProjectById(projectId);
		},
		staleTime: 1000,
	});
};

export const getUserById = (
	userId: string
): Promise<any> => {
	return apiClient.get(`/users/${userId}`);
};

export const getUserQueryOptions = (userId: string) => {
	return queryOptions({
		queryKey: ['user-id', userId],
		queryFn: () => getUserById(userId),
		enabled: false,
	});
};

export const getOrganizationById = (
	organizationId: string
): Promise<any> => {
	return apiClient.get(
		`/organizations/${organizationId}`
	);
};

/* -------------------------------------------------------------------------- */
/*                                    Hooks                                   */
/* -------------------------------------------------------------------------- */
export const useAdditionalityProject = ({
	projectId,
	queryConfig,
}: UseAdditionalityProjectOptions) => {
	const { getUser } = useStore();
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const userData: any = getUser();
	const { t } = useTranslation();

	const {
		data,
		isFetching,
		isFetched,
		isRefetching,
		isError,
	} = useQuery({
		...getAdditionalityProjectQueryOptions(
			projectId,
			userData
		),
		...queryConfig,
	});

	let countryName = '';
	let technolgyProject: any = '';
	let cover = '';

	let sites = [];

	if (data && isFetched && !isError) {
		if (data.technology) {
			technolgyProject = getTechnologyProject(
				data.technology
			);
			cover =
				data && data.cover
					? data.cover
					: technolgyProject
						? technolgyProject.image
						: '';
		}

		if (data.location.countryCode)
			countryName = getCountryName(
				data.location.countryCode
			);

		if (data.organization.sites) {
			sites = data.organization.sites.map(
				(_site: TSite) => {
					return {
						id: _site.id,
						name: _site.name,
						unit: _site.unit
							? getUnitLabel(_site.unit.toLowerCase())
							: NOT_APPLICABLE,
						place: _site.location
							? _site.location.place
							: NOT_APPLICABLE,
						countryCode: _site.location
							? _site.location.countryCode
							: NOT_APPLICABLE,
						conversionFactors:
							_site.conversionFactors &&
							Array.isArray(_site.conversionFactors)
								? _site.conversionFactors.map(
										(_conversionFactor: any) => {
											return {
												id: _conversionFactor.id,
												ratio: _conversionFactor.ratio,
												startDate:
													_conversionFactor.startDate
														? formatDate(
																_conversionFactor.startDate,
																'DD/MM/YYYY'
															)
														: NOT_APPLICABLE,
												unit: _conversionFactor.unit
													? getUnitLabel(
															_conversionFactor.unit.toLowerCase()
														)
													: NOT_APPLICABLE,
											};
										}
									)
								: [],
						meterPoints:
							_site.meterPoints &&
							Array.isArray(_site.meterPoints)
								? _site.meterPoints.map(
										(_meterPoint: any) => {
											return {
												id: _meterPoint.id,
												serialNumber:
													_meterPoint.serialNumber
														? _meterPoint.serialNumber
														: NOT_APPLICABLE,
												countryCode: _meterPoint.location
													? _meterPoint.location
															.countryCode
													: NOT_APPLICABLE,
												place: _meterPoint.location
													? _meterPoint.location.place
													: NOT_APPLICABLE,
												unit: _meterPoint.unit
													? getUnitLabel(
															_meterPoint.unit.toLowerCase()
														)
													: NOT_APPLICABLE,
											};
										}
									)
								: [],
					};
				}
			);
		}
	}

	const { data: user } = useQuery({
		queryKey: [
			'user',
			data &&
				data.organization &&
				data.organization.owner,
		],
		queryFn: () => getUserById(data.organization.owner),
		enabled: false,
	});

	const { data: contact } = useQuery({
		queryKey: [
			'contact',
			data && data.entity && data.entity.contact,
		],
		queryFn: () => getUserById(data.entity.contact),
		enabled: false,
	});

	const currencyUnit = getCurrencyUnit('EUR');
	const unlimited =
		data && data?.financing && data.financing.unlimited;

	const targetEuroValue = unlimited
		? i18n.t('global.unlimited')
		: ((data &&
				data?.financing &&
				data?.financing &&
				data?.financing?.target_euro) ??
			0);

	const coverageValue =
		(data &&
			data?.financing &&
			data?.financing?.pledged_euro) ??
		0;

	return {
		data: {
			...data,
			supportedBy: data?.supportedBy?.logo,
			country: {
				name: countryName,
				code: data?.location?.countryCode.toLowerCase(),
				place: data?.location?.place,
			},
			technology: technolgyProject.label
				? technolgyProject.label
				: NOT_APPLICABLE,
			cover,
			unit:
				data && data.unit
					? getUnitLabel(data.unit.toLowerCase())
					: NOT_APPLICABLE,

			targetEuro: `${unlimited ? t('global.unlimited') : formatNumberWithSpaces(targetEuroValue)} ${
				unlimited ? '' : currencyUnit
			}`,
			coverage: `${formatNumberWithSpaces(coverageValue)} ${currencyUnit}`,
			dueDate:
				data && data.dueDate
					? formatDate(data.dueDate, 'DD/MM/YYYY')
					: NOT_APPLICABLE,
		},
		sites: [],
		user: {},

		contactProducer: {},
		isFetching,
		isFetched,
		isRefetching,
		isError,
	};
};
